import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearningContext } from './LearningLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningSession } from '../../hooks/useLearningSession';
import { learningService } from '../../services/learningService';
import { ClickableText } from '../../components/ClickableWord';
import XpToastContainer, { useXpToast } from '../../components/learning/XpToast';
import LetterPicker from '../../components/learning/LetterPicker';
import InputModeToggle from '../../components/learning/InputModeToggle';
import { useDictationInputMode } from '../../hooks/useDictationInputMode';
import { usePhonicsErrorCollector } from '../../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../../components/learning/PhonicsSessionSummary';
import { LoopyBubble } from '../../components/loopy';
import './QuizPage.css';
import './SentenceNav.css';

// Answer record for each question
interface QuizAnswerRecord {
  questionIndex: number;
  questionId: string;
  questionType: string;
  selectedAnswer: string | string[] | null;
  isCorrect: boolean;
  answeredAt: string; // ISO timestamp
}

// Normalize text for comparison: remove diacritics, lowercase, trim
const normalizeForComparison = (text: string): string => {
  return text
    .normalize('NFD')                    // Decompose accented characters (é → e + ́)
    .replace(/[\u0300-\u036f]/g, '')     // Remove diacritical marks
    .toLowerCase()
    .trim();
};

interface LocalQuizQuestion {
  id: string;
  type: 'multiple_choice' | 'fill_blank' | 'sentence_order' | 'true_false' | 'matching';
  question: string;
  questionZh?: string;
  options?: string[];
  correctAnswer: string | string[];
  explanation?: string;
  explanationZh?: string;
}

interface QuestionResult {
  questionId: string;
  questionType: string;
  isCorrect: boolean;
  relatedWords: string[];
}

const QuizPage: React.FC = () => {
  const { t } = useTranslation('learning');
  const { courseId } = useParams<{ courseId: string }>();
  const { courseData, loading, error } = useLearningContext();
  const { user } = useAuth();
  const { toasts: xpToasts, showXp } = useXpToast();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[] | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [fillBlankInput, setFillBlankInput] = useState('');
  const [sentenceOrder, setSentenceOrder] = useState<string[]>([]);
  const [questionResults, setQuestionResults] = useState<QuestionResult[]>([]);
  const [masterySaved, setMasterySaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // New state for persistent progress
  const [savedAnswers, setSavedAnswers] = useState<QuizAnswerRecord[]>([]);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [hasResumedProgress, setHasResumedProgress] = useState(false);
  const { isLetterPicker, toggle: toggleInputMode } = useDictationInputMode();
  const { addErrors: addPhonicsErrors } = usePhonicsErrorCollector(courseId, 'quiz');
  const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);
  const { handleLetterError, getSessionErrors } = usePhonicsGuidance();

  // Learning session tracking (time spent in this mode)
  const { updateContext: updateSessionContext } = useLearningSession({
    courseId: courseId || '',
    mode: 'quiz',
    enabled: !!courseId && !!user,
    contextData: { quizQuestionIndex: currentIndex },
  });

  // Update session context when question index changes
  useEffect(() => {
    updateSessionContext({ quizQuestionIndex: currentIndex });
  }, [currentIndex, updateSessionContext]);

  // Transform quiz questions from courseData
  const questions: LocalQuizQuestion[] = useMemo(() => {
    if (!courseData?.quizQuestions) return [];

    return courseData.quizQuestions.map((q, index) => {
      // Map question type
      let type: LocalQuizQuestion['type'] = 'multiple_choice';
      if (q.type === 'fill_blank') type = 'fill_blank';
      else if (q.type === 'true_false') type = 'true_false';
      else if (q.type === 'matching') type = 'matching';

      // Transform options
      let options: string[] | undefined;
      if (q.options && Array.isArray(q.options)) {
        options = q.options.map(opt =>
          typeof opt === 'string' ? opt : opt.text
        );
      }

      return {
        id: q.id || String(index),
        type,
        question: q.question_en,
        questionZh: q.question_native,
        options,
        correctAnswer: q.correct_answer,
        explanation: q.explanation_en,
        explanationZh: q.explanation_native,
      };
    });
  }, [courseData?.quizQuestions]);

  const currentQuestion = questions[currentIndex];

  // Load saved quiz progress on mount
  useEffect(() => {
    const loadSavedProgress = async () => {
      if (!courseId || progressLoaded) return;

      try {
        const progress = await learningService.getProgress(courseId);
        const quizProgress = progress?.quiz;

        if (quizProgress?.answers && Array.isArray(quizProgress.answers) && quizProgress.answers.length > 0) {
          const answers = quizProgress.answers as QuizAnswerRecord[];
          setSavedAnswers(answers);

          // Calculate score from saved answers
          const correctCount = answers.filter(a => a.isCorrect).length;
          setScore(correctCount);

          // If quiz was completed, show results
          if (quizProgress.completed) {
            setCurrentIndex(answers.length > 0 ? answers.length - 1 : 0);
            setShowResult(true);
            setMasterySaved(true);
          } else if (answers.length > 0) {
            // Resume from last answered question (show next unanswered)
            setCurrentIndex(answers.length);
            setHasResumedProgress(true);
          }

          // Rebuild questionResults for mastery saving
          const results: QuestionResult[] = answers.map(ans => ({
            questionId: ans.questionId,
            questionType: ans.questionType,
            isCorrect: ans.isCorrect,
            relatedWords: [], // Will be populated when saving mastery
          }));
          setQuestionResults(results);
        }
      } catch (err) {
        console.error('Failed to load quiz progress:', err);
      } finally {
        setProgressLoaded(true);
      }
    };

    loadSavedProgress();
  }, [courseId, progressLoaded]);

  // Save quiz progress after each answer
  const saveQuizProgress = useCallback(async (
    answers: QuizAnswerRecord[],
    completed: boolean,
    finalScore: number
  ) => {
    if (!courseId) return;

    try {
      await learningService.updateProgress(courseId, {
        mode: 'quiz',
        data: {
          quiz: {
            completed,
            score: finalScore,
            answers,
          },
        },
      });
      console.log('Quiz progress saved:', { answersCount: answers.length, completed, score: finalScore });
    } catch (err) {
      console.error('Failed to save quiz progress:', err);
    }
  }, [courseId]);

  const checkAnswer = () => {
    if (!currentQuestion) return false;

    if (currentQuestion.type === 'multiple_choice' || currentQuestion.type === 'true_false') {
      return selectedAnswer === currentQuestion.correctAnswer;
    } else if (currentQuestion.type === 'fill_blank') {
      const correctAnswerStr = Array.isArray(currentQuestion.correctAnswer)
        ? currentQuestion.correctAnswer[0]
        : currentQuestion.correctAnswer;
      return normalizeForComparison(fillBlankInput) === normalizeForComparison(correctAnswerStr);
    } else if (currentQuestion.type === 'sentence_order' || currentQuestion.type === 'matching') {
      return JSON.stringify(sentenceOrder) ===
        JSON.stringify(currentQuestion.correctAnswer);
    }
    return false;
  };

  // Extract words from question text and options
  const extractWordsFromQuestion = useCallback((question: LocalQuizQuestion): string[] => {
    const words: Set<string> = new Set();

    // Extract from question text (filter out common words)
    const questionWords = question.question.split(/\s+/)
      .map(w => w.toLowerCase().replace(/[^\w']/g, ''))
      .filter(w => w.length > 3); // Only words > 3 chars
    questionWords.forEach(w => words.add(w));

    // Extract from correct answer
    if (typeof question.correctAnswer === 'string') {
      const answerWords = question.correctAnswer.split(/\s+/)
        .map(w => w.toLowerCase().replace(/[^\w']/g, ''))
        .filter(w => w.length > 3);
      answerWords.forEach(w => words.add(w));
    } else if (Array.isArray(question.correctAnswer)) {
      question.correctAnswer.forEach(ans => {
        const ansWords = ans.split(/\s+/)
          .map(w => w.toLowerCase().replace(/[^\w']/g, ''))
          .filter(w => w.length > 3);
        ansWords.forEach(w => words.add(w));
      });
    }

    return Array.from(words);
  }, []);

  const handleSubmit = () => {
    if (!currentQuestion) return;

    const isCorrect = checkAnswer();
    const newScore = isCorrect ? score + 1 : score;
    if (isCorrect) {
      setScore(newScore);
    }

    // Record question result for mastery
    const result: QuestionResult = {
      questionId: currentQuestion.id,
      questionType: currentQuestion.type,
      isCorrect,
      relatedWords: extractWordsFromQuestion(currentQuestion),
    };
    setQuestionResults(prev => [...prev, result]);

    // Create answer record for progress persistence
    let userAnswer: string | string[] | null = selectedAnswer;
    if (currentQuestion.type === 'fill_blank') {
      userAnswer = fillBlankInput;
    } else if (currentQuestion.type === 'sentence_order' || currentQuestion.type === 'matching') {
      userAnswer = sentenceOrder;
    }

    const answerRecord: QuizAnswerRecord = {
      questionIndex: currentIndex,
      questionId: currentQuestion.id,
      questionType: currentQuestion.type,
      selectedAnswer: userAnswer,
      isCorrect,
      answeredAt: new Date().toISOString(),
    };

    // Update saved answers
    const newSavedAnswers = [...savedAnswers, answerRecord];
    setSavedAnswers(newSavedAnswers);

    // Save progress after each answer
    const isLastQuestion = currentIndex === questions.length - 1;
    const scorePercentage = Math.round((newScore / questions.length) * 100);
    saveQuizProgress(newSavedAnswers, isLastQuestion, scorePercentage);

    setShowResult(true);
  };

  // Save quiz results to API
  const saveQuizResults = useCallback(async () => {
    if (!courseId || questionResults.length === 0) return;

    setIsSaving(true);
    try {
      const result = await learningService.saveQuizResults({
        courseId,
        totalQuestions: questions.length,
        correctAnswers: score,
        questionResults,
      });
      setMasterySaved(true);
      // Show XP toast
      if (result.xpEarned > 0) {
        showXp(result.xpEarned, result.leveledUp, result.newLevel);
      }
      console.log('Quiz mastery saved successfully');
    } catch (err) {
      console.error('Failed to save quiz results:', err);
    } finally {
      setIsSaving(false);
    }
  }, [courseId, questionResults, questions.length, score, showXp]);

  const handleLetterPickerComplete = useCallback((userText: string) => {
    if (!currentQuestion) return;

    // Set state for display purposes
    setFillBlankInput(userText);

    // Check answer directly with the passed-in text (don't rely on state)
    const correctAnswerStr = Array.isArray(currentQuestion.correctAnswer)
      ? currentQuestion.correctAnswer[0]
      : currentQuestion.correctAnswer;
    const isCorrect = normalizeForComparison(userText) === normalizeForComparison(correctAnswerStr);
    const newScore = isCorrect ? score + 1 : score;
    if (isCorrect) {
      setScore(newScore);
    }

    // Record question result
    const result: QuestionResult = {
      questionId: currentQuestion.id,
      questionType: currentQuestion.type,
      isCorrect,
      relatedWords: extractWordsFromQuestion(currentQuestion),
    };
    setQuestionResults(prev => [...prev, result]);

    // Create answer record
    const answerRecord: QuizAnswerRecord = {
      questionIndex: currentIndex,
      questionId: currentQuestion.id,
      questionType: currentQuestion.type,
      selectedAnswer: userText,
      isCorrect,
      answeredAt: new Date().toISOString(),
    };

    const newSavedAnswers = [...savedAnswers, answerRecord];
    setSavedAnswers(newSavedAnswers);

    // Save progress
    const isLastQuestion = currentIndex === questions.length - 1;
    const scorePercentage = Math.round((newScore / questions.length) * 100);
    saveQuizProgress(newSavedAnswers, isLastQuestion, scorePercentage);

    setShowResult(true);
  }, [currentQuestion, score, currentIndex, savedAnswers, questions.length, extractWordsFromQuestion, saveQuizProgress]);

  const handleNext = () => {
    setSelectedAnswer(null);
    setFillBlankInput('');
    setSentenceOrder([]);
    setShowResult(false);
    setLetterPickerResetKey(k => k + 1);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSkipQuestion = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setSelectedAnswer(null);
      setFillBlankInput('');
      setSentenceOrder([]);
      setShowResult(false);
      setLetterPickerResetKey(k => k + 1);
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, questions.length]);

  const canSkipQuestion = currentIndex < questions.length - 1;

  const handleSentenceOrderClick = (word: string) => {
    if (sentenceOrder.includes(word)) {
      setSentenceOrder(sentenceOrder.filter(w => w !== word));
    } else {
      setSentenceOrder([...sentenceOrder, word]);
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'multiple_choice':
      case 'true_false':
        return (
          <div className="quiz-options">
            {currentQuestion.options?.map((option, idx) => (
              <button
                key={idx}
                className={`quiz-option ${selectedAnswer === option ? 'quiz-option--selected' : ''}
                  ${showResult && option === currentQuestion.correctAnswer ? 'quiz-option--correct' : ''}
                  ${showResult && selectedAnswer === option && option !== currentQuestion.correctAnswer ? 'quiz-option--incorrect' : ''}`}
                onClick={() => !showResult && setSelectedAnswer(option)}
                disabled={showResult}
              >
                <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                <span className="option-text"><ClickableText text={option} /></span>
              </button>
            ))}
          </div>
        );

      case 'fill_blank': {
        const correctAnswerStr = Array.isArray(currentQuestion.correctAnswer)
          ? currentQuestion.correctAnswer[0]
          : currentQuestion.correctAnswer;
        return (
          <div className="fill-blank-container">
            {isLetterPicker && !showResult ? (
              <LetterPicker
                expectedText={correctAnswerStr}
                onComplete={handleLetterPickerComplete}
                onErrorData={addPhonicsErrors}
                onLetterError={handleLetterError}
                disabled={showResult}
                resetKey={letterPickerResetKey}
              />
            ) : (
              <input
                type="text"
                className={`fill-blank-input ${showResult ? (checkAnswer() ? 'fill-blank-input--correct' : 'fill-blank-input--incorrect') : ''}`}
                value={fillBlankInput}
                onChange={(e) => setFillBlankInput(e.target.value)}
                placeholder={t('quiz.fillBlank.placeholder')}
                disabled={showResult}
              />
            )}
            {showResult && (
              <div className="correct-answer-display">
                {t('quiz.fillBlank.correctAnswer', {
                  answer: Array.isArray(currentQuestion.correctAnswer)
                    ? currentQuestion.correctAnswer.join(' / ')
                    : currentQuestion.correctAnswer
                })}
              </div>
            )}
          </div>
        );
      }

      case 'sentence_order':
      case 'matching':
        return (
          <div className="sentence-order-container">
            <div className="word-bank">
              {currentQuestion.options?.map((word, idx) => (
                <button
                  key={idx}
                  className={`word-chip ${sentenceOrder.includes(word) ? 'word-chip--selected' : ''}`}
                  onClick={() => !showResult && handleSentenceOrderClick(word)}
                  disabled={showResult}
                >
                  {word}
                </button>
              ))}
            </div>
            <div className="sentence-display">
              <p className="sentence-label">{t('quiz.sentenceOrder.yourAnswer')}</p>
              <div className={`sentence-box ${showResult ? (checkAnswer() ? 'sentence-box--correct' : 'sentence-box--incorrect') : ''}`}>
                {sentenceOrder.length > 0 ? sentenceOrder.join(' ') : t('quiz.sentenceOrder.placeholder')}
              </div>
            </div>
            {showResult && (
              <div className="correct-answer-display">
                {t('quiz.sentenceOrder.correct', { answer: (currentQuestion.correctAnswer as string[]).join(' ') })}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state (wait for both course data and progress to load)
  if (loading || !progressLoaded) {
    return (
      <div className="quiz-page">
        <div className="quiz-loading">{t('quiz.loading')}</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="quiz-page">
        <div className="quiz-error">
          <h3>{t('quiz.errorTitle')}</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No quiz questions available
  if (questions.length === 0) {
    return (
      <div className="quiz-page">
        <div className="quiz-empty">
          <h3>{t('quiz.noQuestionsTitle')}</h3>
          <p>{t('quiz.noQuestionsDesc')}</p>
        </div>
      </div>
    );
  }

  const isLastQuestion = currentIndex === questions.length - 1;
  const quizComplete = isLastQuestion && showResult;

  return (
    <div className="quiz-page">
      <div className="learning-mode-header">
        <h2 className="learning-mode-header__title">{t('quiz.pageTitle')}</h2>
        {currentQuestion?.type === 'fill_blank' && <InputModeToggle isLetterPicker={isLetterPicker} onToggle={() => { toggleInputMode(); setLetterPickerResetKey(k => k + 1); }} />}
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={() => { if (currentIndex > 0) { setSelectedAnswer(null); setFillBlankInput(''); setSentenceOrder([]); setShowResult(false); setLetterPickerResetKey(k => k + 1); setCurrentIndex(currentIndex - 1); } }}
            disabled={currentIndex === 0}
            aria-label={t('navigation.previousQuestion')}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {questions.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={handleSkipQuestion}
            disabled={!canSkipQuestion}
            aria-label={t('navigation.nextQuestion')}
          >
            ▶
          </button>
        </div>
      </div>

      <div className="quiz-scroll-area">
      {/* Resume notification */}
      {hasResumedProgress && savedAnswers.length > 0 && !showResult && (
        <div className="quiz-resume-banner">
          <span>📝 {t('quiz.resumeBanner', { completed: savedAnswers.length, total: questions.length })}</span>
          <button
            className="quiz-restart-small"
            onClick={() => {
              setCurrentIndex(0);
              setScore(0);
              setSelectedAnswer(null);
              setShowResult(false);
              setFillBlankInput('');
              setSentenceOrder([]);
              setQuestionResults([]);
              setSavedAnswers([]);
              setHasResumedProgress(false);
              saveQuizProgress([], false, 0);
            }}
          >
            {t('quiz.restart')}
          </button>
        </div>
      )}

      <div className="quiz-content">
        {!quizComplete ? (
          <>
            <div className="question-card">
              <p className="question-text"><ClickableText text={currentQuestion?.question || ''} /></p>
              {currentQuestion?.questionZh && (
                <p className="question-text-zh">{currentQuestion.questionZh}</p>
              )}
              {renderQuestion()}

              {showResult && currentQuestion?.explanation && (
                <LoopyBubble
                  state={checkAnswer() ? 'correct' : 'error'}
                  emotion={checkAnswer() ? 'celebrate' : 'pouting'}
                  title={checkAnswer() ? t('quiz.result.correct') : t('quiz.result.incorrect')}
                  className="explanation-box--loopy"
                >
                  <p className="explanation-text"><ClickableText text={currentQuestion.explanation || ''} /></p>
                  {currentQuestion.explanationZh && (
                    <p className="explanation-text-zh">{currentQuestion.explanationZh}</p>
                  )}
                </LoopyBubble>
              )}
            </div>

            <div className="quiz-actions">
              {!showResult ? (
                <>
                  {!(isLetterPicker && currentQuestion?.type === 'fill_blank') && <button
                    className="quiz-button quiz-button--submit"
                    onClick={handleSubmit}
                    disabled={
                      ((currentQuestion?.type === 'multiple_choice' || currentQuestion?.type === 'true_false') && !selectedAnswer) ||
                      (currentQuestion?.type === 'fill_blank' && !fillBlankInput) ||
                      ((currentQuestion?.type === 'sentence_order' || currentQuestion?.type === 'matching') && sentenceOrder.length === 0)
                    }
                  >
                    {t('quiz.submit')}
                  </button>}
                </>
              ) : (
                <button
                  className="quiz-button quiz-button--next"
                  onClick={handleNext}
                >
                  {isLastQuestion ? t('quiz.viewResults') : t('quiz.next')}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="quiz-results">
            <h3>{t('quiz.complete.title')}</h3>
            <div className="final-score">
              <div className="score-circle">
                <span className="score-number">{Math.round((score / questions.length) * 100)}%</span>
              </div>
              <p className="score-text">
                {t('quiz.complete.scoreText', { score, total: questions.length })}
              </p>
            </div>

            {/* Status indicators */}
            {isSaving && (
              <p className="saving-indicator">{t('quiz.complete.saving')}</p>
            )}
            {masterySaved && (
              <p className="saved-indicator">✓ {t('quiz.complete.saved')}</p>
            )}

            <PhonicsSessionSummary errors={getSessionErrors()} />

            {/* Action buttons */}
            <div className="quiz-results-actions">
              {!masterySaved && !isSaving && (
                <button
                  className="quiz-button quiz-button--save"
                  onClick={saveQuizResults}
                >
                  💾 {t('quiz.saveProgress')}
                </button>
              )}
              <button
                className="quiz-button quiz-button--restart"
                onClick={() => {
                  setCurrentIndex(0);
                  setScore(0);
                  setSelectedAnswer(null);
                  setShowResult(false);
                  setFillBlankInput('');
                  setSentenceOrder([]);
                  setQuestionResults([]);
                  setMasterySaved(false);
                  // Clear saved progress
                  setSavedAnswers([]);
                  setHasResumedProgress(false);
                  // Save empty progress to reset
                  saveQuizProgress([], false, 0);
                }}
              >
                {t('quiz.retake')}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
      <XpToastContainer toasts={xpToasts} />
    </div>
  );
};

export default QuizPage;
